import { Button } from "PFComponents/button";
import { Select } from "PFComponents/select/select";
import { TextArea } from "PFComponents/text/text_area";
import Toggle from "PFComponents/toggle/toggle";
import { useBookingApprovalApprove } from "PFCore/hooks/queries/bookings/approvals/use_booking_approval_approve";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { BookingApproval } from "PFTypes";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./modals.module.scss";
import ApprovalModal from "./parts/approval_modal";
import ApprovalTable from "./parts/approval_table";

type IProps = {
  data: BookingApproval | BookingApproval[];
  handleClose: () => void;
  onSuccess?: (items?: BookingApproval[]) => void;
};

const Content = ({ data, handleClose, onSuccess }: IProps) => {
  const arrifiedData = [data].flat();
  const approvableIds = arrifiedData.filter(({ state }) => state === "pending").map(({ id }) => id);
  const { approve } = useBookingApprovalApprove(...approvableIds);
  const { bookingCategories } = useBookingCategories();

  const categoriesOptions = useMemo(
    () =>
      bookingCategories
        .filter((category) => !category.readonly && !category.archived)
        .map(({ id, display_as }) => ({
          id,
          item: id,
          displayElement: display_as
        })),
    [bookingCategories]
  );
  const { t } = useTranslation(["bookingModule", "translation"]);

  const [note, setNote] = useState<string>();
  const [updateCategory, setUpdateCategory] = useState(false);
  const [category, setCategory] = useState(null);

  const handleSubmit = () => {
    const payload: { note: string | null; booking_category_id?: number } = { note: note || null };
    if (updateCategory && category) {
      payload.booking_category_id = category;
    }

    approve(payload).then(() => {
      handleClose();
      onSuccess && onSuccess(arrifiedData);
    });
  };

  return (
    <>
      <h3 className={css.title}>{t("bookingModule:approvals.approveBooking")}</h3>
      <ApprovalTable data={data} />
      <div className={css.inputWrapper}>
        <div className={css.label}>{t("bookingModule:approvals.note")}</div>
        <TextArea className={css.textArea} value={note} onChange={setNote} maxLength={280} maxHeight={200} />
      </div>
      <div>
        <div
          className={css.inputWrapper}
          style={{
            display: "flex",
            flexWrap: "nowrap",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Toggle
            inline
            reversed
            description={t("bookingModule:approvals.updateCategory")}
            checked={updateCategory}
            onChange={() => setUpdateCategory((curr) => !curr)}
          />

          <Select
            // @ts-ignore
            style={{ width: "40%", maxWidth: 300 }}
            disabled={!updateCategory}
            label={t("bookingModule:approvals.selectBookingCategory")}
            value={categoriesOptions.find(({ id }) => id === category)?.displayElement}
            controlledValue
            options={categoriesOptions}
            handleChange={setCategory}
          />
        </div>
      </div>
      <hr className={css.separator} />
      <div className={css.buttons}>
        <Button text={t("translation:cancel")} kind="secondary" onClick={handleClose} />
        <Button
          text={t("translation:approve")}
          kind="primary"
          disabled={(updateCategory && !category) || !note}
          onClick={handleSubmit}
        />
      </div>
    </>
  );
};

const ApproveApprovalModal = ({ handleClose, ...props }: IProps) => (
  <ApprovalModal qaId="bookingApproveApprovalModal" handleClose={handleClose}>
    <Content {...props} handleClose={handleClose} />
  </ApprovalModal>
);

export default ApproveApprovalModal;
